import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import ProcessingModal from './components/ProcessingModal';
import ServiceChargeModal from './components/ServiceChargeModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';
import SegmentedReadAloud from './components/SegmentedReadAloud';

const API = process.env.REACT_APP_BACKEND_URL;

const KSGPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    author_name: '',
    book_title: '',
    target_audience: '',
    custom_target_audience: '',
    author_biography: '',
    speech_duration: '30',  // 10, 20, or 30 minutes
    speaking_style: 'Professional & Engaging'  // New: speaking style/tone
  });

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating keynote speech', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  
  const [showBiographyWarningModal, setShowBiographyWarningModal] = useState(false);

  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Target audience options - Standard publishing audiences
  const audienceOptions = [
    'Children (Ages 0-12)',
    'Young Adult/Teen (Ages 13-18)',
    'New Adult (Ages 18-30)',
    'Adult Fiction Readers (Ages 25-54)',
    'Adult Non-Fiction Readers (Ages 25-54)',
    'Mature/Senior Readers (Ages 55+)',
    'Academic/Professional',
    'General Audience (All Ages)',
    'Custom Audience'
  ];

  // Speaking style options
  const speakingStyleOptions = [
    'Professional & Engaging',
    'Authoritative & Commanding',
    'Academic & Scholarly',
    'Conversational & Relatable',
    'Inspirational & Motivational',
    'Technical & Analytical',
    'Storytelling & Narrative',
    'Humorous & Light-hearted'
  ];

  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadAndDisplayReport(reportId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (result && result.author_name && result.book_title) {
      document.title = `${result.author_name} - ${result.book_title} | KSG Keynote`;
    } else {
      document.title = 'Keynote Speech Generator | Digital Ink AI';
    }
    return () => {
      document.title = 'Digital Ink AI';
    };
  }, [result]);

  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all KSG reports
        const reportsResponse = await axios.get(`${API}/api/ksg/reports`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);

        // Fetch transaction history
        const transactionsResponse = await axios.get(`${API}/api/payments/transactions`, {
          withCredentials: true
        });
        setTransactions(transactionsResponse.data || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const loadAndDisplayReport = async (reportId) => {
    try {
      setIsProcessing(true);
      const response = await axios.get(`${API}/api/ksg/results/${reportId}`, {
        withCredentials: true
      });
      setResult(response.data);
      setIsProcessing(false);
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
    } catch (err) {
      console.error('Failed to load report:', err);
      toast.error('Failed to load report');
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.doc')) {
        setFileError('Please upload a PDF, DOCX, TXT, or DOC file');
        setFile(null);
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setFile(null);
        return;
      }
      setFileError('');
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!file) {
      setError('Please upload a manuscript file');
      return;
    }
    
    if (!formData.author_name.trim() || !formData.book_title.trim()) {
      setError('Please fill in author name and book title');
      return;
    }
    
    // Check if author biography is empty and show warning
    if (!formData.author_biography || !formData.author_biography.trim()) {
      setShowBiographyWarningModal(true);
      return;
    }
    
    // Check if user needs to pay BEFORE processing (only for À La Carte users)
    if (user?.role === 'a_la_carte') {
      setPaymentInfo({
        amount: 15.00,  // Premium service - $15 for a la carte
        currency: 'USD',
        description: 'Keynote Speech Generator - AI-powered professional keynote speeches',
        submission_data: {
          name: 'Keynote Speech Generator',
          bookTitle: formData.book_title || 'KSG Generation'
        },
        name: 'Keynote Speech Generator',
        bookTitle: formData.book_title || 'KSG Generation'
      });
      setShowPaymentModal(true);
      return;
    }
    
    await submitForGeneration();
  };
  
  const handlePaymentSuccess = async (paymentData) => {
    setShowPaymentModal(false);
    
    try {
      await submitForGeneration();
    } catch (error) {
      console.error('Post-payment generation error:', error);
      setError(error.response?.data?.detail || error.message || 'Generation failed. You have NOT been charged.');
    }
  };
  
  const handleProceedWithoutBiography = async () => {
    setShowBiographyWarningModal(false);
    
    // Check if user needs to pay BEFORE processing
    if (user?.role === 'a_la_carte') {
      setPaymentInfo({
        amount: 15.00,  // Premium service - $15 for a la carte
        currency: 'USD',
        description: 'Keynote Speech Generator - AI-powered professional keynote speeches',
        submission_data: {
          name: 'Keynote Speech Generator',
          bookTitle: formData.book_title || 'KSG Generation'
        },
        name: 'Keynote Speech Generator',
        bookTitle: formData.book_title || 'KSG Generation'
      });
      setShowPaymentModal(true);
      return;
    }
    
    await submitForGeneration();
  };

  const submitForGeneration = async () => {
    setIsProcessing(true);
    
    // Step 1: Upload and analyze
    setCurrentStep('Uploading and analyzing manuscript...');
    setProgressSteps([
      { text: 'Analyzing manuscript', completed: false, current: true },
      { text: 'Generating keynote speech', completed: false, current: false },
      { text: 'Formatting results', completed: false, current: false }
    ]);
    setProcessingProgress(10);
    
    const formDataToSend = new FormData();
    formDataToSend.append('manuscript_file', file);
    formDataToSend.append('author_name', formData.author_name);
    formDataToSend.append('book_title', formData.book_title);
    formDataToSend.append('target_audience', formData.target_audience === 'Custom Audience' ? formData.custom_target_audience : formData.target_audience);
    formDataToSend.append('author_biography', formData.author_biography || '');
    formDataToSend.append('speech_duration', formData.speech_duration);
    formDataToSend.append('speaking_style', formData.speaking_style);

    setProcessingProgress(25);
    
    // Step 2: Generate speech
    setCurrentStep('Generating professional keynote speech...');
    setProgressSteps([
      { text: 'Analyzing manuscript', completed: true, current: false },
      { text: 'Generating keynote speech', completed: false, current: true },
      { text: 'Formatting results', completed: false, current: false }
    ]);
    setProcessingProgress(40);

    try {
      const response = await axios.post(
        `${API}/api/ksg/generate`,
        formDataToSend,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      // Step 3: Formatting
      setCurrentStep('Finalizing your keynote speech...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating keynote speech', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);

      setResult(response.data);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating keynote speech', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      setIsProcessing(false);
      toast.success('Keynote speech generated successfully!');
      
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
        setShowEmailReceiptModal(true);
      }
      
    } catch (err) {
      setIsProcessing(false);
      const errorMsg = err.response?.data?.detail || 'Generation failed';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleSendReceipt = async () => {
    try {
      await axios.post(`${API}/api/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user?.email
      }, {
        withCredentials: true
      });
      toast.success('Receipt email sent successfully!');
      setShowEmailReceiptModal(false);
    } catch (err) {
      console.error('Failed to send receipt:', err);
      toast.error('Failed to send receipt email');
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
  };

  const handlePrintResults = () => {
    if (!result) return;
    
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const now = new Date();
      const timestamp = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      const generatedDate = now.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      
      const bookTitle = result.book_title || formData.book_title || 'Untitled Work';
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Digital Ink AI - KSG Keynote</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.6; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .ksg-header { text-align: center; margin-bottom: 20px; }
              .ksg-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .ksg-header h1 { font-size: 16pt; margin: 8px 0; font-family: 'Times New Roman', Times, serif; color: #ea580c; font-style: italic; }
              .ksg-header h2 { font-size: 18pt; margin: 5px 0; font-family: 'Times New Roman', Times, serif; font-weight: bold; }
              .ksg-header p { font-size: 11pt; margin: 3px 0; color: #666; }
              .generated-date { text-align: center; font-size: 10pt; color: #666; margin-top: 15px; }
              .speech-content { margin: 20px 0; padding: 15px; border-left: 4px solid #ea580c; background-color: #fff7ed; border-radius: 6px; page-break-inside: avoid; }
              .speech-content p { margin: 0; font-size: 12pt; line-height: 1.8; font-family: 'Times New Roman', Times, serif; white-space: pre-wrap; }
              .word-count { font-size: 10pt; color: #666; font-style: italic; margin-top: 12px; text-align: right; }
              .footer { margin-top: 20px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } }
            </style>
          </head>
          <body>
            <div class="print-header">
              <div>${timestamp}</div>
              <div>Digital Ink AI - KSG Keynote</div>
            </div>
            
            <div class="ksg-header">
              <img src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" onload="window.print();" />
              <h1>Keynote Speech Generator</h1>
              <h2>"${bookTitle}"</h2>
              <p>by ${result.author_name}</p>
              <p style="font-size: 10pt; margin-top: 8px;">Target Audience: ${result.target_audience}</p>
            </div>
            
            <div class="generated-date">Generated ${generatedDate}</div>
            
            <div class="speech-content">
              <p>${result.keynote_speech || ''}</p>
              ${result.speech_word_count ? `<div class="word-count">Word Count: ${result.speech_word_count} words (~${result.speech_duration || 40} minutes)</div>` : ''}
            </div>
            
            <div class="footer">
              <p>Generated by Digital Ink AI - Keynote Speech Generator</p>
              <p>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p>© 2025 Use What You Got Publishing, LLC. All rights reserved.</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 20;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${result.speech_duration || 40}-Minute Keynote Speech`, margin, yPosition);
    yPosition += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated for: ${result.author_name}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Book: ${result.book_title || formData.book_title}`, margin, yPosition);
    yPosition += 6;
    doc.text(`Audience: ${result.target_audience}`, margin, yPosition);
    yPosition += 15;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const speechLines = doc.splitTextToSize(result.keynote_speech || '', maxWidth);
    speechLines.forEach(line => {
      if (yPosition > 270) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, margin, yPosition);
      yPosition += 7;
    });

    doc.save(`${result.author_name}_Keynote_Speech.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  const handleNewSpeech = () => {
    setResult(null);
    setFormData({
      author_name: '',
      book_title: '',
      target_audience: 'Corporate Executives & Business Leaders',
      author_biography: '',
      speech_duration: '30',
      speaking_style: 'Professional & Engaging'
    });
    setFile(null);
    setError('');
    navigate('/ksg');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            🎤 Keynote Speech Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Command attention and captivate your audience with professionally crafted keynote speeches tailored to your book and brand. Our AI analyzes your manuscript and author profile to generate compelling speeches that showcase your expertise, engage listeners with relevant anecdotes and insights, and create memorable moments that drive book sales and establish your authority as a thought leader in your genre.
          </p>
        </div>

        {/* Pricing Info */}
        {user && (
          <div className="mb-8 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-6">
            <div className="text-center">
              {user.subscription_status !== 'None' || user.role === 'admin' || user.role === 'beta_tester' ? (
                <div>
                  <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                  <p className="text-green-700">Included with your subscription - generate as many keynote speeches as you need!</p>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-purple-900 mb-2">⭐ Premium Service - $15.00</h3>
                  <p className="text-orange-700">Professional keynote speech charged to your payment method on file</p>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6 print:hidden">
            {error}
          </div>
        )}

        {!result ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print:hidden">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <h2 className="text-2xl font-bold text-slate-900">🎤 Speech Details</h2>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </div>

                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Book Title *</label>
                <input
                  type="text"
                  name="book_title"
                  value={formData.book_title}
                  onChange={handleInputChange}
                  placeholder="e.g., The Digital Revolution"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Author Name *</label>
                <input
                  type="text"
                  name="author_name"
                  value={formData.author_name}
                  onChange={handleInputChange}
                  placeholder="e.g., R. Dale Brunet"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Target Audience *</label>
                <select
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="">Select target audience...</option>
                  {audienceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {formData.target_audience === 'Custom Audience' && (
                  <input
                    type="text"
                    name="custom_target_audience"
                    placeholder="Describe your target audience..."
                    value={formData.custom_target_audience || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 mt-2"
                    required
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">Speech will be tailored to this audience</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Speech Duration *</label>
                <select
                  name="speech_duration"
                  value={formData.speech_duration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="10">10 minutes (~1,250-1,500 words)</option>
                  <option value="20">20 minutes (~2,500-3,000 words)</option>
                  <option value="30">30 minutes (~3,750-4,500 words)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Select the desired speech length</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Speaking Style *</label>
                <select
                  name="speaking_style"
                  value={formData.speaking_style}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  required
                >
                  {speakingStyleOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Choose the tone and delivery style for your speech</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Author Biography (Optional but Recommended)
                </label>
                <textarea
                  name="author_biography"
                  value={formData.author_biography}
                  onChange={handleInputChange}
                  placeholder="Provide background about the author (credentials, experience, achievements, etc.). This helps ground the speech in factual information about you."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 resize-vertical"
                  rows="5"
                />
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ <strong>Important:</strong> Leaving this field empty may result in less personalized content. We strongly recommend providing author background for the best results.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Book Manuscript *</label>
                <div className="mt-2 border-2 border-dashed border-orange-300 rounded-lg p-6 text-center bg-orange-50">
                  <input
                    type="file"
                    id="manuscript-file"
                    accept=".pdf,.docx,.txt,.doc"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                  <label htmlFor="manuscript-file" className="cursor-pointer">
                    <div className="text-4xl mb-4">📁</div>
                    {file ? (
                      <div>
                        <p className="text-green-600 font-medium">{file.name}</p>
                        <p className="text-sm text-slate-500">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-slate-600 font-medium">Click to select file</p>
                        <p className="text-sm text-slate-500">PDF, DOCX, TXT, or DOC files only (max 10MB)</p>
                      </div>
                    )}
                  </label>
                  {fileError && <p className="text-sm text-red-600 mt-2">{fileError}</p>}
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Generating Keynote Speech...' : 'Generate Keynote Speech with Digital Ink AI'}
                </button>
              </div>
            </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Generation Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Multiple durations: 10, 20, or 30 minutes</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>8 professional speaking styles to choose from</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>First-person narrative for authentic delivery</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Tailored to your specific audience</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Grounded in your book's actual content</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Professional structure: hook → credibility → insights → action</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-2">✓</span>
                    <span>Downloadable PDF & printable format</span>
                  </div>
                </div>
              </div>

              {/* Speaking Styles */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎭 Speaking Styles</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Professional:</strong> Balanced and credible</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Authoritative:</strong> Commanding presence</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Academic:</strong> Scholarly and research-focused</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Conversational:</strong> Approachable and relatable</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Inspirational:</strong> Uplifting and motivating</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Technical:</strong> Data-driven and analytical</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Storytelling:</strong> Narrative and engaging</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-amber-500 mr-2">•</span>
                    <span><strong>Humorous:</strong> Light and entertaining</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-12 w-auto"
                  />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                    <p className="text-sm text-gray-600">Keynote Speech Generator</p>
                    <p className="text-xs text-gray-600 mt-1">Author: {result.author_name} | Book: {result.book_title}</p>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p>{new Date().toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="border-b pb-6 mb-6 print:border-b-2">
              <div className="flex justify-between items-start mb-4 print:mb-2">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">{result.speech_duration || 40}-Minute Keynote Speech</h2>
                  <p className="text-slate-600 mt-1">By {result.author_name}</p>
                  <p className="text-slate-600 text-sm">Book: {result.book_title}</p>
                  <p className="text-slate-600 text-sm">Audience: {result.target_audience}</p>
                </div>
                <div className="flex gap-3 print:hidden">
                  <button onClick={handlePrintResults} className="hidden md:inline-flex px-4 py-2 border border-purple-600 text-purple-600 hover:bg-purple-50 rounded-lg">🖨️ Print</button>
                  <button onClick={handleExportPDF} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">📄 Export PDF</button>
                  <button onClick={handleNewSpeech} className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">✨ New Speech</button>
                </div>
              </div>
            </div>

            {/* Segmented Read Aloud - BEFORE speech text */}
            <div className="mb-6 print:hidden">
              <SegmentedReadAloud text={result.keynote_speech} speechId={result.id} />
            </div>

            {/* Speech Content */}
            <div className="mb-8 p-6 bg-orange-50 rounded-lg border border-orange-200 page-break-inside-avoid">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-bold text-orange-900">Keynote Speech</h3>
              </div>
              <p className="text-slate-800 whitespace-pre-wrap leading-relaxed">{result.keynote_speech}</p>
              {result.speech_word_count && <p className="text-sm text-slate-600 mt-4">Word Count: {result.speech_word_count} words (~{result.speech_duration || 40} minutes)</p>}
            </div>

            <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-600">
              <p className="font-semibold">Generated by Digital Ink AI - Keynote Speech Generator</p>
              <p>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p className="mt-2">© 2025 Use What You Got Publishing, LLC. All rights reserved.</p>
            </div>
          </div>
        )}
      </main>

      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="Keynote Speech Generator (KSG)" 
        bookTitle={formData.book_title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />
      
      {showPaymentModal && paymentInfo && (
        <ServiceChargeModal 
          isOpen={showPaymentModal} 
          onClose={() => setShowPaymentModal(false)} 
          onConfirm={handlePaymentSuccess} 
          serviceInfo={paymentInfo}
          user={user}
        />
      )}
      
      <EmailReceiptModal 
        isOpen={showEmailReceiptModal} 
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceipt}
        transactionId={lastTransactionId}
        userEmail={user?.email}
      />

      {/* Author Biography Warning Modal */}
      {showBiographyWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">⚠️</div>
              <h3 className="text-xl font-bold text-amber-900 mb-2">Author Biography Not Provided</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-700 mb-4">
                You haven't provided an author biography. While this field is optional, <strong>leaving it empty may result in:</strong>
              </p>
              <ul className="text-sm text-slate-600 space-y-2 mb-4">
                <li className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span>Less personalized and authentic content about the author</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span>Generic credibility statements instead of specific achievements</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-500 mr-2">•</span>
                  <span>Reduced connection between the author's background and speech content</span>
                </li>
              </ul>
              <p className="text-slate-700 font-semibold">
                We strongly recommend adding author biography information for the best results.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowBiographyWarningModal(false)}
                className="flex-1 px-4 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 font-medium"
              >
                Go Back & Add Biography
              </button>
              <button
                onClick={handleProceedWithoutBiography}
                className="flex-1 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 font-medium"
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page {
            margin: 0.75in 0.5in;
            size: auto;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          header, footer, nav, .no-print {
            display: none !important;
          }
          .print\:hidden { display: none !important; }
          .print\:block { display: block !important; }
          .page-break-inside-avoid { page-break-inside: avoid; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
};

export default KSGPage;
